import { canRoleViewNote } from './noteVisibility';
import { enqueueRuntimeEvent } from './outbox';
import type { RuntimeActorRole, StudentNoteRecord, StudentNoteType, StudentNoteVisibility } from './types';

const STORAGE_KEY = 'belajarku:student-notes:v1';
export const STUDENT_NOTES_UPDATED_EVENT = 'belajarku:student-notes:updated';

function makeId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function loadAllStudentNotes(): StudentNoteRecord[] {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const notes = JSON.parse(raw) as StudentNoteRecord[];
    return Array.isArray(notes) ? notes : [];
  } catch {
    return [];
  }
}

function saveAllStudentNotes(notes: StudentNoteRecord[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
  window.dispatchEvent(new CustomEvent(STUDENT_NOTES_UPDATED_EVENT));
}

export function listStudentNotes(studentId: string, viewerRole: RuntimeActorRole): StudentNoteRecord[] {
  return loadAllStudentNotes()
    .filter((note) => note.studentId === studentId && note.status !== 'archived')
    .filter((note) => canRoleViewNote(viewerRole, note))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function addStudentNote(input: {
  studentId: string;
  authorRole: RuntimeActorRole;
  authorName: string;
  body: string;
  visibility: StudentNoteVisibility;
  noteType: StudentNoteType;
  subjectCode?: string;
  skillCode?: string;
  assignmentId?: string;
}): StudentNoteRecord {
  const now = new Date().toISOString();
  const note: StudentNoteRecord = {
    id: makeId('NOTE'),
    studentId: input.studentId,
    authorRole: input.authorRole,
    authorName: input.authorName,
    body: input.body.trim(),
    visibility: input.visibility,
    noteType: input.noteType,
    status: 'active',
    createdAt: now,
    updatedAt: now,
    ...(input.subjectCode ? { subjectCode: input.subjectCode } : {}),
    ...(input.skillCode ? { skillCode: input.skillCode } : {}),
    ...(input.assignmentId ? { assignmentId: input.assignmentId } : {}),
  };
  const notes = [note, ...loadAllStudentNotes()];
  saveAllStudentNotes(notes);
  enqueueRuntimeEvent({
    id: `OUTBOX-${note.id}`,
    eventType: 'student_note_created',
    studentId: note.studentId,
    payload: note,
  });
  return note;
}
